import { useState } from 'react';
import { useCreate, useInvalidate } from '@refinedev/core';
import { Loader2 } from 'lucide-react';
import { notify } from '@/lib/notify';
import { Button } from '@/components/ui/button';
import { ColorPicker } from '@/components/ui/color-picker';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { TagItem } from '../types';

const HEX_REGEX = /^#[0-9a-fA-F]{6}$/;
const DEFAULT_TAG_COLOR = '#6366f1';

interface TagCreateFormData {
  name: string;
  color: string;
  description: string;
}

interface CreateTagValues {
  name: string;
  color: string;
  description?: string;
}

interface TagCreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (tag: TagItem) => void;
  title?: string;
  description?: string;
  successMessage?: string;
}

export function TagCreateDialog({
  open,
  onOpenChange,
  onCreated,
  title = 'Создать тег',
  description = 'Новый тег сразу появится в списке доступных тегов.',
  successMessage = 'Тег создан',
}: TagCreateDialogProps) {
  const invalidate = useInvalidate();
  const createMutation = useCreate<TagItem>();
  const [formData, setFormData] = useState<TagCreateFormData>({
    name: '',
    color: DEFAULT_TAG_COLOR,
    description: '',
  });
  const [colorError, setColorError] = useState<string | null>(null);

  const isCreating = createMutation.mutation.isPending;

  const resetForm = () => {
    setFormData({ name: '', color: DEFAULT_TAG_COLOR, description: '' });
    setColorError(null);
  };

  const handleChange = <K extends keyof TagCreateFormData>(field: K, value: TagCreateFormData[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (field === 'color') {
      setColorError(null);
    }
  };

  const handleSubmit = () => {
    const name = formData.name.trim();
    const descriptionValue = formData.description.trim();

    if (!name) {
      notify.error('Введите название тега');
      return;
    }
    if (!HEX_REGEX.test(formData.color)) {
      setColorError('Введите корректный hex-цвет (например #6366f1)');
      return;
    }

    const values: CreateTagValues = descriptionValue
      ? { name, color: formData.color, description: descriptionValue }
      : { name, color: formData.color };

    createMutation.mutate(
      { resource: 'tags', values, invalidates: [], successNotification: false },
      {
        onSuccess: (result) => {
          const createdTag = result.data;
          resetForm();
          onOpenChange(false);
          onCreated?.(createdTag);
          notify.success(successMessage);
          invalidate({ resource: 'tags', invalidates: ['list'] });
        },
        onError: (error) => {
          notify.error(error.message ?? 'Ошибка создания тега');
        },
      },
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        onOpenChange(nextOpen);
        if (!nextOpen && !isCreating) {
          resetForm();
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="tag-create-name">Название *</Label>
            <Input
              id="tag-create-name"
              placeholder="Новый лид"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              disabled={isCreating}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSubmit();
                }
              }}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="tag-create-description">Описание</Label>
            <Textarea
              id="tag-create-description"
              placeholder="Необязательное описание тега..."
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              disabled={isCreating}
              rows={3}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="tag-create-color">Цвет *</Label>
            <ColorPicker
              id="tag-create-color"
              value={formData.color}
              onChange={(color) => handleChange('color', color)}
              disabled={isCreating}
              invalid={!!colorError}
            />
            {colorError && (
              <p className="text-sm text-destructive">{colorError}</p>
            )}
            <p className="text-xs text-muted-foreground">Hex-формат: #rrggbb</p>
          </div>
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isCreating}
          >
            Отмена
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={isCreating}>
            {isCreating && <Loader2 className="h-4 w-4 animate-spin" />}
            Создать
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
